import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

export default function UploadDropzone({
  fileInputRef,
  files = [],
  disabled,
  accept,
  maxSizeMB,
  multiple = true,
  onFilesChange,
  onFilesDrop,
  onRemoveFile,
  onClearAll,
}) {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const hasFiles = files && files.length > 0;

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const droppedFiles = Array.from(event.dataTransfer.files || []);
    if (droppedFiles.length) onFilesDrop?.(droppedFiles);
  };

  const dragSx = {
    borderColor: isDragging ? 'primary.main' : alpha(theme.palette.primary.main, 0.35),
    bgcolor: isDragging
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.02),
  };

  if (!hasFiles) {
    return (
      <Paper
        variant="outlined"
        component="label"
        htmlFor="audio-upload"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'block',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'center',
          borderRadius: 3,
          borderStyle: 'dashed',
          borderWidth: 1.5,
          p: { xs: 4, md: 5 },
          transition:
            'border-color 160ms ease, background-color 160ms ease',
          ...dragSx,
          '&:hover': !disabled && {
            borderColor: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={onFilesChange}
          style={{ display: 'none' }}
          id="audio-upload"
          disabled={disabled}
        />
        <CloudUploadOutlinedIcon
          sx={{
            fontSize: 44,
            color: 'primary.main',
            mb: 1,
            transform: isDragging ? 'translateY(-2px)' : 'none',
            transition: 'transform 160ms ease',
          }}
        />
        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {isDragging
            ? 'Dateien hier ablegen'
            : multiple
            ? 'Dateien hier ablegen oder auswählen'
            : 'Datei hier ablegen oder auswählen'}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ fontSize: '0.85rem', mt: 0.5 }}
        >
          WAV, MP3, M4A, MP4, FLAC, OGG, WEBM, AAC · max. {maxSizeMB} MB pro Datei
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        p: 2,
        borderRadius: 3,
        borderStyle: isDragging ? 'dashed' : 'solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
        transition: 'border-color 160ms ease, background-color 160ms ease',
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {files.length} Datei{files.length === 1 ? '' : 'en'} ausgewählt
        </Typography>
        <Button
          size="small"
          component="label"
          htmlFor="audio-upload"
          disabled={disabled}
        >
          Hinzufügen
        </Button>
        <Button size="small" onClick={onClearAll} disabled={disabled} color="inherit">
          Alle entfernen
        </Button>
      </Stack>

      <Box
        component="label"
        htmlFor="audio-upload"
        sx={{
          display: 'block',
          mb: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: isDragging ? 'primary.main' : alpha(theme.palette.primary.main, 0.24),
          bgcolor: alpha(theme.palette.primary.main, isDragging ? 0.08 : 0.035),
          color: isDragging ? 'primary.main' : 'text.secondary',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'center',
          fontSize: '0.8rem',
          fontWeight: 600,
        }}
      >
        {isDragging ? 'Weitere Dateien hier ablegen' : 'Weitere Dateien hier ablegen oder hinzufügen'}
      </Box>

      <Stack spacing={1}>
        {files.map((entry, idx) => (
          <Stack
            key={`${entry.file.name}-${idx}`}
            direction="row"
            alignItems="center"
            gap={1.5}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <InsertDriveFileOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: '0.9rem', fontWeight: 500 }}
                noWrap
                title={entry.file.name}
              >
                {entry.file.name}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {formatSize(entry.file.size)}
                {entry.status ? ` · ${entry.status}` : ''}
              </Typography>
            </Box>
            <Tooltip title="Entfernen">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onRemoveFile?.(idx)}
                  disabled={disabled || entry.locked}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onFilesChange}
        style={{ display: 'none' }}
        id="audio-upload"
      />
    </Paper>
  );
}
